'use client';
import React from 'react';

import { CountrySelect } from '../CountrySelect';
import { useUpdateRecaptcha } from '@components/Recaptcha/Recaptcha';
import { Modal } from '@components/Modal';
import { Link } from '@components/Link';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useParams } from 'next/navigation';
import { hasCaptchaSiteKey } from '@utils/getCaptchaSiteKey';
import { LANGUAGE_OPTIONS } from '@components/IntlProvider/IntlProvider';
import { isValidPhoneNumber } from '@utils/validation.utils';

import { requestLeadGenerationAPI } from '@services/launches/requestLeadGeneration';
import SVGIcons from '@components/SVGIcon';
import { useLeadSourceParamsLaunchTeaser } from '@hooks/useLeadSourceParamsLaunchTeaser';
import { logEvent } from '@utils/googleAnalytics';
import { getCountryCodeAndNameFromValue } from '@services/properties/getLeadGenerationOptions';
import { NoTranslateWrapper } from '@components/NoTranslateWrapper';

import useLeadMetaQuery from '@hooks/querys/useLeadMeta';
import cn from './style.module.scss';

type FormType = {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  contact: string;
  buyingReason: string;
  consent: boolean;
  city: string;
  responseKey?: string;
  comment?: string;
  availability?: string;
  receiveUpdates?: boolean;
};

const DEFAULT_FORM_VALUES = {
  countryCode: '+971___United Arab Emirates',
  buyingReason: '',
  consent: false,
  firstName: '',
  lastName: '',
  email: '',
  contact: '',
  responseKey: '',
  comment: '',
  city: '',
  receiveUpdates: false,
};

const DEFAULT_GA_KEYS = {
  category: 'Aldar Websites',
  trigger: 'Damascus',
};

interface LeadFormProps {
  countryCodes: Record<string, string>[];
  nationalities: Record<string, string>[];
  successPopUpLink: string;
  privacyPolicyLink: string;
  termsAndConditionsLink: string;
  t: { [key: string]: any };
}

export function LeadForm(props: LeadFormProps) {
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'success'>(
    'idle'
  );
  const { lang } = useParams();

  const leadMeta = useLeadMetaQuery(lang as string).data;
  const cityOfResidence = leadMeta?.cityOfResidence ?? [];
  const { getLeadSourceParamsLaunchTeaser } = useLeadSourceParamsLaunchTeaser();
  const { t } = props;
  const isFormStarted = React.useRef(false);
  const { getRecaptchaToken } = useUpdateRecaptcha();

  const schema = yup.object({
    firstName: yup
      .string()
      .required('First name is required')
      .max(35, 'First name should be 35 characters long')
      .matches(
        /^[A-Za-z\s\u0600-\u06FF]+$/,
        'First name should contain only letters and spaces'
      )
      .trim(),
    lastName: yup
      .string()
      .required('Last name is required')
      .max(35, 'Last name should be 35 characters long')
      .matches(
        /^[A-Za-z\s\u0600-\u06FF]+$/,
        'Last name should contain only letters and spaces'
      )
      .trim(),
    email: yup
      .string()
      .required('Email is required')
      .email('Invalid email')
      .matches(
        /^[a-zA-Z0-9]{1,}[a-zA-Z0-9._+-]*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
        'Invalid email'
      )
      .trim(),
    countryCode: yup.string().required().trim(),
    contact: yup
      .string()
      .required('Phone number is required')
      .test(
        'Phone Number Validity Check',
        'Not a valid phone number',
        function (number: string) {
          const { code } = getCountryCodeAndNameFromValue(
            this.parent.countryCode
          );
          return isValidPhoneNumber(number, code);
        }
      )
      .test(
        'Phone Number Leading Zero validation check',
        'Please remove the leading 0 from your phone number.',
        function (number: string) {
          return !number.trim().startsWith('0');
        }
      )
      .matches(/^\d{1,20}$/, 'Phone number must be digits only')
      .trim(),
    city: yup.string().required('City of residence is required'),
    buyingReason: yup.string().required('This field is required'),
    availability: t['availabilityOptions']
      ? yup.string().required('This field is required')
      : yup.string().optional(),
    consent: yup.boolean().required(),
    receiveUpdates: yup.boolean().optional(),
    responseKey: yup.string(),
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    trigger,
    setError,
    unregister,
    reset,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: yupResolver(schema),
  });

  const onSelectCountryCode = (value: string) => {
    setValue('countryCode', value);
    trigger('contact');
  };

  const handleFormStart = () => {
    if (isFormStarted.current) return;
    isFormStarted.current = true;
    logEvent({
      event: 'form_start',
      additionalInfo: {
        form_id: 'gardenia-bay-launch-lead-form',
        form_type: 'gardenia-bay-launch-lead-form',
      },
    });
  };
  const onSubmitForm: SubmitHandler<FormType> = async (data) => {
    setStatus('loading');
    if (!data.consent) {
      setStatus('idle');
      setError('consent', {
        message:
          'Please agree to the Privacy Policy & Terms of Use to proceed.',
      });
      return;
    }
    const responseKey = await getRecaptchaToken();
    if (hasCaptchaSiteKey() && !responseKey) {
      setStatus('idle');
      setError('responseKey', {
        message: 'Please refresh the page and try again.',
      });
      return;
    }
    try {
      const paramsKeys = getLeadSourceParamsLaunchTeaser();
      const response = await requestLeadGenerationAPI({
        ...data,
        comment: data.comment ? data.comment : data.availability,
        ...paramsKeys,
        responseKey: responseKey,
        countryCode: getCountryCodeAndNameFromValue(data.countryCode).code,
        language: LANGUAGE_OPTIONS.find(({ value }) => value === lang)?.label,
        project: 'Gardenia Bay',
      }).getResponse();

      logEvent({
        event: 'form_success',
        additionalInfo: {
          lead_id: response?.data?.leadId,
          form_id: 'gardenia-bay-launch-lead-form',
          form_type: 'gardenia-bay-launch-lead-form',
          Project_Name: 'Gardenia Bay',
          Trigger: paramsKeys.enquiryTrigger ?? DEFAULT_GA_KEYS.trigger,
          Category: paramsKeys.enquiryCategory ?? DEFAULT_GA_KEYS.category,
        },
      });
      setStatus('success');
    } catch (e: any) {
      setStatus('idle');
      setError('responseKey', {
        message: `${e.message ?? 'Lead generation failed!'} Please try again.`,
      });
    }
  };

  const commonProps = { disabled: status === 'loading' ? true : false };

  React.useEffect(() => {
    if (!hasCaptchaSiteKey()) {
      unregister('responseKey');
    }
  }, []);
  const handleFormClose = () => {
    setStatus('idle');
    reset(undefined, { keepDefaultValues: true });
  };

  return (
    <>
      <NoTranslateWrapper>
        <div
          className={cn.registerGRDForms}
          onBlur={handleFormStart}
          id="launchGRDContact"
        >
          <ul className={cn.registerGRDList} id="launchYASRegister">
            <li>
              <div className={cn.registerGRDSides}>
                <div className={cn.registerGRDSides__element}>
                  <input
                    type="text"
                    id="fname"
                    data-testid="lf_firstName"
                    placeholder={`${t['firstName']} *`}
                    {...register('firstName')}
                    {...commonProps}
                  />
                  <p className={cn.validationGRDMessage}>
                    {errors.firstName?.message}
                  </p>
                </div>
                <div className={cn.registerGRDSides__element}>
                  <input
                    type="text"
                    id="lname"
                    data-testid="lf_lastName"
                    placeholder={`${t['lastName']} *`}
                    {...register('lastName')}
                    {...commonProps}
                  />
                  <p className={cn.validationGRDMessage}>
                    {errors.lastName?.message}
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className={cn.registerGRDSides__element}>
                <input
                  type="text"
                  id="email"
                  data-testid="lf_email"
                  placeholder={`${t['email']} *`}
                  {...register('email')}
                  {...commonProps}
                />
                <p className={cn.validationGRDMessage}>
                  {errors.email?.message}
                </p>
              </div>
            </li>
            <li>
              <div className={cn.registerGRDItem}>
                <div className={cn.elementGRDPhone}>
                  <div className={cn.phoneGRDCountry}>
                    <Controller
                      name="countryCode"
                      control={control}
                      render={({ field }) => (
                        <CountrySelect
                          {...field}
                          onChange={onSelectCountryCode}
                          options={props.countryCodes}
                          {...commonProps}
                        />
                      )}
                    />
                  </div>
                  <div
                    className={cn.phoneGRDItem}
                    onBlur={() => trigger('contact')}
                  >
                    <input
                      type="tel"
                      data-testid="lf_contact"
                      placeholder={`${t['phoneNumber']} *`}
                      {...register('contact')}
                      {...commonProps}
                    />
                  </div>
                </div>
                <p className={cn.validationGRDMessage}>
                  {errors.contact?.message}
                </p>
              </div>
            </li>
            <li>
              <div className={cn.registerGRDItem}>
                <select
                  id="city"
                  data-testid="lf_city"
                  {...register('city')}
                  placeholder={`${t['city']} *`}
                  {...commonProps}
                >
                  <NoTranslateWrapper>
                    <option
                      style={{
                        color: '#455564',
                        lineHeight: '22px',
                        opacity: '.5',
                      }}
                      value=""
                      disabled
                    >
                      {`${t['city']} *`}
                    </option>
                  </NoTranslateWrapper>
                  {cityOfResidence?.map((option) => (
                    <NoTranslateWrapper key={option.value}>
                      <option
                        style={{ color: '#455564', lineHeight: '22px' }}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    </NoTranslateWrapper>
                  ))}
                </select>
                {errors.city?.message && (
                  <p className={cn.validationGRDMessage}>
                    {errors.city?.message}
                  </p>
                )}
              </div>
            </li>
            <li>
              <div className={cn.registerGRDItem}>
                <select
                  id="buyingReason"
                  data-testid="lf_buyingReason"
                  defaultValue={''}
                  {...register('buyingReason')}
                  placeholder={`${t['reasonToBuy']} *`}
                  {...commonProps}
                >
                  <NoTranslateWrapper>
                    <option style={{ color: '#455564' }} value="" disabled>
                      {`${t['reasonToBuy']} *`}
                    </option>
                  </NoTranslateWrapper>
                  <NoTranslateWrapper>
                    <option style={{ color: '#455564' }} value="Investor">
                      {t['reasonBuyOptionInvestor']}
                    </option>
                  </NoTranslateWrapper>
                  <NoTranslateWrapper>
                    <option style={{ color: '#455564' }} value="End User">
                      {t['reasonBuyOptionHomeOwner']}
                    </option>
                  </NoTranslateWrapper>
                </select>
                <p className={cn.validationGRDMessage}>
                  {errors.buyingReason?.message}
                </p>
              </div>
            </li>
            {t['availabilityOptions'] && (
              <li>
                <div className={cn.registerGRDItem}>
                  <select
                    id="availability"
                    data-testid="lf_availability"
                    defaultValue={''}
                    {...register('availability')}
                    placeholder={`${t['availability']} *`}
                    {...commonProps}
                  >
                    {t['availabilityOptions']?.map(
                      (option: { value: string; label: string }) => (
                        <NoTranslateWrapper key={option.value}>
                          <option
                            style={{
                              color: '#455564',
                              lineHeight: '22px',
                              direction: 'ltr',
                            }}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        </NoTranslateWrapper>
                      )
                    )}
                  </select>
                  {errors.availability?.message && (
                    <p className={cn.validationGRDMessage}>
                      {errors.availability?.message}
                    </p>
                  )}
                </div>
              </li>
            )}
            <li>
              <div className={cn.authGRDWrapper}>
                <div className={cn.formsGRDAuth}>
                  <label className={cn.customGRDCheckbox}>
                    <input
                      type="checkbox"
                      id="receiveUpdates"
                      data-testid="lf_receiveUpdates"
                      {...register('receiveUpdates')}
                      {...commonProps}
                    />
                    <span className={cn.checkGRDMark}></span>
                  </label>
                  <p>{t['recieveUpdates']}</p>
                </div>
                <div className={cn.formsGRDAuth}>
                  <label className={cn.customGRDCheckbox}>
                    <input
                      type="checkbox"
                      id="consent"
                      data-testid="lf_consent"
                      value="true"
                      {...register('consent')}
                      {...commonProps}
                    />
                    <span className={cn.checkGRDMark}></span>
                  </label>
                  <p>
                    <span>{t['consentLabel']} </span>
                    <Link
                      url={`https://www.aldar.com/${lang}/privacy-policy`}
                      target={'_blank'}
                    >
                      {t['privacyPolicy']}
                    </Link>
                    <span>{` & `}</span>
                    <Link
                      url={`https://www.aldar.com/${lang}/terms-and-conditions`}
                      target={'_blank'}
                    >
                      {t['termsAndConditions']}
                    </Link>
                    <span> {t['consentLabelEnd']}</span>
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className={cn.reGRDCaptcha}>
                <p className={cn.validationGRDMessage}>
                  {errors.responseKey?.message}
                </p>
                <p className={cn.validationGRDMessage}>
                  {errors.consent?.message}
                </p>
              </div>
            </li>
            <li>
              <div className={cn.launchGRDControls}>
                <div className={cn.formGRDSubmit}>
                  <button
                    className={cn.registerGRDButton}
                    type="submit"
                    data-testid="lf_submit"
                    onClick={handleSubmit(onSubmitForm)}
                    {...commonProps}
                  >
                    {!commonProps.disabled ? t['actionButton'] : 'Loading...'}
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </NoTranslateWrapper>
      <Modal
        isOpen={status === 'success'}
        onClose={handleFormClose}
        className={cn.registerGRDSuccess}
      >
        <div data-notranslate className={cn.registerGRDMessage}>
          <div className={cn.closeGRDBtn}>
            <span
              className={cn.closeGRDBtn__icon}
              onClick={handleFormClose}
              data-testid="close-icon"
            >
              <SVGIcons type={'cross-svg'} />
            </span>
          </div>
          <div className={cn.registerGRDInner}>
            <h2> {t['successHeading']}</h2>
            <div className={cn.registerGRDInner__paragraph}>
              <p>{`${t['successContent']}`}</p>
            </div>
            <div className={cn.registerGRDInner__button}>
              <a
                href={props.successPopUpLink}
                target="_blank" rel="noopener noreferrer"
                className={cn.registerGRDButton}
              >
                {t['successButton']}
              </a>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
